import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { environment } from '../../../../environments/environment';
import { SETTINGS } from '../../../core/config/common.settings';
import { EncryptionService } from '../../../core/services/encryption';
import { DividerModule } from 'primeng/divider';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressBarModule } from 'primeng/progressbar';
import { UserService, User } from '../../../core/services/user.service';
import { ProductService, Product } from '../../../core/services/product.service';
import { TerritoryService, GroupedTerritories } from '../../../core/services/territory.service';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { COUNTRIES, CITIZENSHIPS, Country, Citizenship } from '../../../core/config/country-data.constants';
import { FileService } from '../../../core/services/file';

interface Role {
  _id: string;
  identification: string;
  name: string;
  level: string;
  subscriberId?: {
    _id: string;
    companyName: string;
  };
}

interface Subscriber {
  _id: string;
  companyName: string;
}

interface CurrentUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  roleId: {
    _id: string;
    identification: string;
    level: string;
  };
  subscriberId?: {
    _id: string;
    companyName: string;
  };
}

interface TerritoryItem {
  _id: string;
  name: string;
  status: string;
}

interface ProductItem {
  _id: string;
  name: string;
  code: string;
  isActive: boolean;
}

@Component({
  selector: 'app-add-edit-user',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CardModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    ToastModule,
    DividerModule,
    AccordionModule,
    CheckboxModule,
    ProgressSpinnerModule,
    SkeletonModule,
    ProgressBarModule
  ],
  providers: [MessageService],
  templateUrl: './add-edit-user.html'
})
export class AddEditUser implements OnInit {
  userForm: FormGroup;
  roles: Role[] = [];
  subscribers: Subscriber[] = [];
  loading = false;
  submitting = false;
  currentUser: CurrentUser | null = null;
  loggedInUserName: string = '';
  loggedInUserFirstName: string = '';
  loggedInUserLastName: string = '';
  loggedInUserEmail: string = '';
  loggedInUserRole: string = '';
  loggedInUserID: string = '';
  
  // Edit mode properties
  isEditMode = false;
  editingUserId: string | null = null;
  editingUser: User | null = null;

  // Products and Territories
  products: ProductItem[] = [];
  territoriesGrouped: GroupedTerritories = {};
  selectedProductIds: string[] = [];
  selectedTerritoryIds: string[] = [];
  loadingProducts = false;
  loadingTerritories = false;
  loadingRoles = false;
  loadingSubscribers = false;
  showProductsAndTerritories = false;

  // File upload properties for ID Card (only in edit mode)
  isFileUploading = false;
  uploadedFileUrl: string | null = null;
  uploadedFile: File | null = null;
  dragOver: boolean = false;

  // Field name mapping for better error messages
  fieldDisplayNames: { [key: string]: string } = {
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email address',
    roleId: 'User role',
    companyName: 'Company name',
    subscriberId: 'Subscriber',
    title: 'Title',
    gender: 'Gender',
    mobileNumber: 'Mobile number',
    streetAddress: 'Street address',
    houseNumber: 'House number',
    postcode: 'Postcode',
    city: 'City',
    country: 'Country',
    citizenship: 'Citizenship',
    nationalInsuranceNo: 'National Insurance number',
    tradeLicence: 'Trade licence',
    idCard: 'ID card'
  };

  // Bio information dropdown data (only used in edit mode)
  countries: Country[] = COUNTRIES;
  citizenships: Citizenship[] = CITIZENSHIPS;
  titles = [
    { label: 'Mr.', value: 'Mr' },
    { label: 'Mrs.', value: 'Mrs' },
    { label: 'Ms.', value: 'Ms' },
    { label: 'Miss', value: 'Miss' },
    { label: 'Dr.', value: 'Dr' },
    { label: 'Prof.', value: 'Prof' },
    { label: 'Sir', value: 'Sir' },
    { label: 'Lady', value: 'Lady' },
    { label: 'Lord', value: 'Lord' },
    { label: 'Dame', value: 'Dame' },
    { label: 'Rev.', value: 'Rev' },
    { label: 'Fr.', value: 'Fr' },
    { label: 'Sr.', value: 'Sr' },
    { label: 'Br.', value: 'Br' },
    { label: 'Capt.', value: 'Capt' },
    { label: 'Col.', value: 'Col' },
    { label: 'Gen.', value: 'Gen' },
    { label: 'Lt.', value: 'Lt' },
    { label: 'Maj.', value: 'Maj' },
    { label: 'Sgt.', value: 'Sgt' },
    { label: 'Adm.', value: 'Adm' },
    { label: 'Amb.', value: 'Amb' },
    { label: 'Atty.', value: 'Atty' },
    { label: 'Eng.', value: 'Eng' },
    { label: 'Hon.', value: 'Hon' },
    { label: 'Pres.', value: 'Pres' },
    { label: 'Gov.', value: 'Gov' },
    { label: 'Sen.', value: 'Sen' },
    { label: 'Rep.', value: 'Rep' },
    { label: 'Judge', value: 'Judge' },
    { label: 'Justice', value: 'Justice' },
    { label: 'Bishop', value: 'Bishop' },
    { label: 'Archbishop', value: 'Archbishop' },
    { label: 'Cardinal', value: 'Cardinal' },
    { label: 'Pope', value: 'Pope' },
    { label: 'Imam', value: 'Imam' },
    { label: 'Rabbi', value: 'Rabbi' },
    { label: 'Swami', value: 'Swami' },
    { label: 'Guru', value: 'Guru' },
    { label: 'Sensei', value: 'Sensei' },
    { label: 'Master', value: 'Master' },
    { label: 'Chief', value: 'Chief' },
    { label: 'Sheikh', value: 'Sheikh' },
    { label: 'Emir', value: 'Emir' },
    { label: 'Sultan', value: 'Sultan' },
    { label: 'King', value: 'King' },
    { label: 'Queen', value: 'Queen' },
    { label: 'Prince', value: 'Prince' },
    { label: 'Princess', value: 'Princess' },
    { label: 'Duke', value: 'Duke' },
    { label: 'Duchess', value: 'Duchess' },
    { label: 'Earl', value: 'Earl' },
    { label: 'Count', value: 'Count' },
    { label: 'Countess', value: 'Countess' },
    { label: 'Viscount', value: 'Viscount' },
    { label: 'Baron', value: 'Baron' },
    { label: 'Baroness', value: 'Baroness' },
    { label: 'Marquis', value: 'Marquis' },
    { label: 'Marquess', value: 'Marquess' },
    { label: 'Knight', value: 'Knight' },
    { label: 'Esq.', value: 'Esq' }
  ];

  genders = [
    { label: 'Male', value: 'MALE' },
    { label: 'Female', value: 'FEMALE' },
    { label: 'Non-binary', value: 'NON_BINARY' }
  ];

  tradeLicences = [
    { label: 'Yes', value: 'YES' },
    { label: 'No', value: 'NO' }
  ];

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private encryption: EncryptionService,
    private userService: UserService,
    private productService: ProductService,
    private territoryService: TerritoryService,
    private subscriptionContextService: SubscriptionContextService,
    private fileService: FileService
  ) {
    this.userForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      roleId: ['', Validators.required],
      companyName: [''],
      subscriberId: [''],
      products: [[]],
      territories: [[]],
      // Bio information fields (only used in edit mode)
      title: [''],
      gender: [''],
      mobileNumber: [''],
      streetAddress: [''],
      houseNumber: [''],
      postcode: [''],
      city: [''],
      country: [''],
      citizenship: [''],
      nationalInsuranceNo: [''],
      taxNumber: [''],
      tradeLicence: [''],
      idCard: ['']
    });
    

  }

  ngOnInit() {
    this.loadCurrentUser();
    this.checkEditMode();
    
    // Add a delay to ensure form validation is properly initialized
    setTimeout(() => {
      if (this.isEditMode) {
        this.refreshFormValidation();
      }
    }, 500);
  }

  checkEditMode() {
    this.route.queryParams.subscribe(params => {
      if (params['edit'] && params['userId']) {
        this.isEditMode = true;
        this.editingUserId = params['userId'];
        this.loadUserForEdit();
      }
    });
  }

  loadUserForEdit() {
    if (!this.editingUserId) return;
    
    this.loading = true;
    this.userService.getUserById(this.editingUserId).subscribe({
      next: (user) => {
        this.editingUser = user;
        this.populateFormWithUserData(user);
        this.loading = false;
        
        // Check if user is trying to edit someone with a higher role
        if (this.isEditingHigherRoleUser(user)) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Permission Warning',
            detail: 'You cannot edit users with higher roles than your own.'
          });
          setTimeout(() => {
            this.router.navigate(['/admin/user-management']);
          }, 2000);
        }
      },
      error: (error) => {
        console.error('Error loading user for edit:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user data for editing.'
        });
        this.loading = false;
        this.router.navigate(['/admin/user-management']);
      }
    });
  }

  populateFormWithUserData(user: User) {
    this.userForm.patchValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      roleId: user.roleId?._id || '',
      companyName: user.subscriberId?.companyName || '',
      subscriberId: user.subscriberId?._id || '',
      // Bio information fields (only in edit mode)
      title: user.title || '',
      gender: user.gender || '',
      mobileNumber: user.mobileNumber || '',
      streetAddress: user.address?.streetAddress || '',
      houseNumber: user.address?.houseNumber || '',
      postcode: user.address?.postcode || '',
      city: user.address?.city || '',
      country: user.address?.country || '',
      citizenship: user.citizenship || '',
      nationalInsuranceNo: user.nationalInsuranceNo || '',
      taxNumber: user.taxNumber || '',
      tradeLicence: user.tradeLicence || '',
      idCard: user.idCard || ''
    });

    // Set uploaded file URL if user has an ID card
    if (user.idCard) {
      this.uploadedFileUrl = user.idCard;
    }
    
    // Set selected products and territories from user data
    this.selectedProductIds = user.products?.map((p: any) => typeof p === 'string' ? p : p._id) || [];
    this.selectedTerritoryIds = user.territories?.map((t: any) => typeof t === 'string' ? t : t._id) || [];
    
    this.userForm.patchValue({
      products: this.selectedProductIds,
      territories: this.selectedTerritoryIds
    });
    
    // In edit mode, email field should be read-only as it's used for identification
    if (this.isEditMode) {
      this.userForm.get('email')?.disable();
    }
    
    // Trigger role change logic to set up proper validation for company name
    if (user.roleId?._id) {
      this.onRoleChange({ value: user.roleId._id });
      
      // For FSR roles in edit mode, ensure products and territories are loaded and visible
      if (user.roleId.identification === 'FSR') {
        console.log('FSR role detected in edit mode, ensuring products and territories are loaded');
        this.showProductsAndTerritories = true;
        
        // Load products and territories if not already loaded
        if (this.products.length === 0 && !this.loadingProducts) {
          console.log('Loading products for FSR in edit mode');
          this.loadProductsForSubscriber();
        }
        if (Object.keys(this.territoriesGrouped).length === 0 && !this.loadingTerritories) {
          console.log('Loading territories for FSR in edit mode');
          this.loadTerritoriesForSubscriber();
        }
      }
      
      // Force refresh validation after a short delay to ensure all validators are properly set
      setTimeout(() => {
        this.refreshFormValidation();
      }, 200);
    }
  }

  loadCurrentUser() : void {
      const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      console.log(encryptedUserStr);
  
      if (encryptedUserStr) {
        const userStr = this.encryption.decrypt(encryptedUserStr);
        console.log(userStr);
        const user = JSON.parse(userStr);
        this.loggedInUserName = user.userName;
        this.loggedInUserFirstName = user.firstName;
        this.loggedInUserLastName = user.lastName;
        this.loggedInUserEmail = user.email;
        this.loggedInUserRole = user.role.identification;
        this.loggedInUserID = user._id;
        
        // Set the currentUser object for role hierarchy checks
        this.currentUser = {
          _id: user._id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          roleId: {
            _id: user.role._id,
            identification: user.role.identification,
            level: user.role.level
          },
          subscriberId: user.subscriberId ? {
            _id: user.subscriberId._id,
            companyName: user.subscriberId.companyName
          } : undefined
        };
        
        // Load available roles and subscribers after setting currentUser
        this.loadAvailableRoles();
        this.loadSubscribers();
        
        // Log subscriber ID information for debugging
        if (this.currentUser?.subscriberId) {
          console.log('Logged-in user has subscriber ID:', this.currentUser.subscriberId);
        } else {
          console.log('Logged-in user does not have a subscriber ID');
        }
        
        // Load products and territories only if available roles don't have CSM or SM
      
      }
  
      console.log(this.loggedInUserName);
      console.log(this.loggedInUserRole);
      console.log(this.loggedInUserEmail);
      console.log(this.loggedInUserFirstName);
      console.log(this.loggedInUserLastName);
    }
  

  loadAvailableRoles() {
    // Use currentUser if available, otherwise fall back to loggedInUserRole
    const currentRole = this.currentUser?.roleId?.identification || this.loggedInUserRole;
    
    if (!currentRole) return;

    let url = `${environment.apiUrl}/roles/available`;
    
    this.loadingRoles = true;
    this.http.get<{message: string, roles: Role[]}>(url)
      .subscribe({
        next: (response) => {
          // Filter roles based on hierarchy
          this.roles = this.filterRolesByHierarchy(response.roles);
          console.log('Available roles after filtering:', this.roles.map(r => ({ name: r.name, identification: r.identification })));
          this.loadingRoles = false;
        },
        error: (error) => {
          console.error('Error loading available roles:', error);
          
          // Extract the actual error message from the API response
          let errorMessage = 'Failed to load available roles. Please try again.';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
          this.loadingRoles = false;
        }
      });
  }

  /**
   * Filter roles based on current user's role hierarchy
   * Only allow inviting roles that are lower in the hierarchy
   */
  private filterRolesByHierarchy(availableRoles: Role[]): Role[] {
    // Use currentUser if available, otherwise fall back to loggedInUserRole
    const currentRole = this.currentUser?.roleId?.identification || this.loggedInUserRole;
    
    console.log('Current user role:', currentRole);
    console.log('Available roles before filtering:', availableRoles.map(r => ({ name: r.name, identification: r.identification })));
    
    if (!currentRole) return [];

    // Define role hierarchy (from highest to lowest level)
    const roleHierarchy = {
      'SUPER_DEV_ADMIN': 6, // Highest level
      'CSM': 5,
      'SM': 4, 
      'SA': 3,
      'A': 2,
      'FSR': 1  // Lowest level
    };
    
    const currentUserLevel = roleHierarchy[currentRole as keyof typeof roleHierarchy];
    console.log('Current user level:', currentUserLevel);
    
    // Filter roles to only include those lower in hierarchy than current user
    const filteredRoles = availableRoles.filter(role => {
      const roleLevel = roleHierarchy[role.identification as keyof typeof roleHierarchy];
      
      // Hide SA role in edit mode
      if (this.isEditMode && role.identification === 'SA') {
        console.log('Hiding SA role in edit mode');
        return false;
      }
      
      const isLowerLevel = roleLevel && roleLevel < currentUserLevel;
      console.log(`Role ${role.identification} (level ${roleLevel}) vs current user level ${currentUserLevel}: ${isLowerLevel ? 'included' : 'excluded'}`);
      
      return isLowerLevel;
    });
    
    console.log('Filtered roles:', filteredRoles.map(r => ({ name: r.name, identification: r.identification })));
    return filteredRoles;
  }

  loadSubscribers() {
    // Use currentUser if available, otherwise fall back to loggedInUserRole
    const currentRole = this.currentUser?.roleId?.identification || this.loggedInUserRole;
    
    if (!currentRole) return;

    this.loadingSubscribers = true;
    this.http.get<{message: string, subscribers: Subscriber[]}>(`${environment.apiUrl}/users/subscribers`)
      .subscribe({
        next: (response) => {
          this.subscribers = response.subscribers;
          
          // Pre-populate subscriber ID if logged-in user has one and we're not in edit mode
          if (!this.isEditMode && this.currentUser?.subscriberId?._id) {
            this.userForm.patchValue({
              subscriberId: this.currentUser.subscriberId._id
            });
            console.log('Pre-populated subscriber ID:', this.currentUser.subscriberId._id);
          }
          this.loadingSubscribers = false;
        },
        error: (error) => {
          console.error('Error loading subscribers:', error);
          
          // Extract the actual error message from the API response
          let errorMessage = 'Failed to load subscribers. Some features may be limited.';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: errorMessage
          });
          this.loadingSubscribers = false;
        }
      });
  }

  onSubmit() {
    // Log current form state for debugging
    console.log('Form submission attempt:', {
      formValid: this.userForm.valid,
      customValid: this.isFormValidForSubmission(),
      formValues: this.userForm.value,
      formErrors: this.userForm.errors,
      companyNameField: {
        value: this.userForm.get('companyName')?.value,
        valid: this.userForm.get('companyName')?.valid,
        errors: this.userForm.get('companyName')?.errors,
        touched: this.userForm.get('companyName')?.touched
      },
      isSARole: this.isSARoleSelected()
    });
    
    // Use custom validation instead of form.valid
    if (this.isFormValidForSubmission()) {
      // Check if user is trying to assign a higher role than their own
      if (this.isEditMode && this.isAssigningHigherRole()) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Permission Warning',
          detail: 'You cannot assign roles higher than your own.'
        });
        return;
      }
      
      this.submitting = true;
      const formValues = this.userForm.value;
      
      // Build formData object
      const formData: any = {
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        email: formValues.email,
        roleId: formValues.roleId,
        products: this.selectedProductIds,
        territories: this.selectedTerritoryIds,
        addedBy: this.loggedInUserID
      };
      
      // Handle subscriberId - if it's empty string, set it to null or omit it
      if (formValues.subscriberId && formValues.subscriberId !== '') {
        formData.subscriberId = formValues.subscriberId;
      }
      
      // Handle companyName - only include if SA role is selected
      if (this.isSARoleSelected() && formValues.companyName && formValues.companyName !== '') {
        formData.companyName = formValues.companyName;
      }
      
      // In edit mode, include bio information fields
      if (this.isEditMode) {
        // Bio fields at top level
        if (formValues.title) formData.title = formValues.title;
        if (formValues.gender) formData.gender = formValues.gender;
        if (formValues.mobileNumber) formData.mobileNumber = formValues.mobileNumber;
        if (formValues.citizenship) formData.citizenship = formValues.citizenship;
        if (formValues.nationalInsuranceNo) formData.nationalInsuranceNo = formValues.nationalInsuranceNo;
        if (formValues.taxNumber) formData.taxNumber = formValues.taxNumber;
        if (formValues.tradeLicence) formData.tradeLicence = formValues.tradeLicence;
        // Use uploadedFileUrl if available, otherwise use form value (manual URL entry)
        if (this.uploadedFileUrl) {
          formData.idCard = this.uploadedFileUrl;
        } else if (formValues.idCard) {
          formData.idCard = formValues.idCard;
        }
        
        // Address object
        if (formValues.streetAddress || formValues.houseNumber || formValues.postcode || formValues.city || formValues.country) {
          formData.address = {
            streetAddress: formValues.streetAddress || '',
            houseNumber: formValues.houseNumber || '',
            postcode: formValues.postcode || '',
            city: formValues.city || '',
            country: formValues.country || ''
          };
        }
      }
      
      // Subscriber ID will be automatically added by the auth interceptor
      console.log('Subscriber ID will be automatically added by auth interceptor');
      
      // Debug logging
      console.log('Submitting form data:', formData);
      console.log('Current user ID:', this.loggedInUserID);
      console.log('Current user subscriber ID:', this.currentUser?.subscriberId?._id);
      console.log('Is edit mode:', this.isEditMode);

      if (this.isEditMode && this.editingUserId) {
        // For edit mode, re-enable email field to get its value
        const emailValue = this.userForm.get('email')?.value;
        this.userForm.get('email')?.enable();
        
        this.userService.updateUser(this.editingUserId, formData).subscribe({
          next: (response: any) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'User updated successfully!'
            });
            this.submitting = false;
            setTimeout(() => {
              this.router.navigate(['/admin/user-management']);
            }, 2000);
          },
          error: (error) => {
            console.error('Error updating user:', error);
            let errorMessage = 'Failed to update user. Please try again.';
            
            if (error.error?.message) {
              errorMessage = error.error.message;
            } else if (error.error?.emailCheck) {
              errorMessage = 'A user with this email already exists.';
            }
            
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: errorMessage
            });
            this.submitting = false;
          }
        });
      } else {
        this.userService.inviteUser(formData)
          .subscribe({
            next: (response: any) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'User invited successfully! An email with temporary password has been sent.'
              });
              this.userForm.reset();
              this.submitting = false;
              
              // Navigate back to user list after a short delay
              setTimeout(() => {
                this.router.navigate(['/admin/user-management']);
              }, 2000);
            },
            error: (error) => {
              console.error('Error inviting user:', error);
              let errorMessage = 'Failed to invite user. Please try again.';
              
              if (error.error?.message) {
                errorMessage = error.error.message;
              } else if (error.error?.emailCheck) {
                errorMessage = 'A user with this email already exists.';
              }
              
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage
              });
              this.submitting = false;
            }
          });
      }
    } else {
      this.markFormGroupTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields correctly.'
      });
    }
  }

  markFormGroupTouched() {
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      control?.markAsTouched();
    });
  }

  onRoleChange(event: any) {
    const selectedRole = this.roles.find(role => role._id === event.value);
    if (selectedRole) {
      // If role requires subscriber, ensure subscriberId is required
      if (selectedRole.level === 'SUBSCRIBER' && !this.userForm.get('subscriberId')?.value) {
        this.userForm.get('subscriberId')?.setValidators([Validators.required]);
      } else {
        this.userForm.get('subscriberId')?.clearValidators();
      }
      this.userForm.get('subscriberId')?.updateValueAndValidity();

      // Handle company name validation for SA role
      if (selectedRole.identification === 'SA') {
        this.userForm.get('companyName')?.setValidators([Validators.required, Validators.minLength(5)]);
        // Don't clear the value if it already exists
      } else {
        this.userForm.get('companyName')?.clearValidators();
        this.userForm.get('companyName')?.setValue('');
      }
      this.userForm.get('companyName')?.updateValueAndValidity();
      
      // Handle products and territories visibility for FSR role
      console.log('Role selected:', selectedRole.identification, 'FSR check:', selectedRole.identification === 'FSR');
      if (selectedRole.identification === 'FSR') {
        console.log('Setting showProductsAndTerritories to true');
        this.showProductsAndTerritories = true;
        // Load products and territories if not already loaded
        if (this.products.length === 0 && !this.loadingProducts) {
          console.log('Loading products for FSR');
          this.loadProductsForSubscriber();
        }
        if (Object.keys(this.territoriesGrouped).length === 0 && !this.loadingTerritories) {
          console.log('Loading territories for FSR');
          this.loadTerritoriesForSubscriber();
        }
      } else {
        console.log('Setting showProductsAndTerritories to false for role:', selectedRole.identification);
        this.showProductsAndTerritories = false;
        // Clear selections when switching away from FSR (but preserve in edit mode if user already has selections)
        if (!this.isEditMode) {
          this.selectedProductIds = [];
          this.selectedTerritoryIds = [];
          this.userForm.patchValue({ 
            products: this.selectedProductIds,
            territories: this.selectedTerritoryIds
          });
        }
      }
      
      // Update the entire form's validity state
      this.userForm.updateValueAndValidity();
      
      // Force refresh validation state
      setTimeout(() => {
        this.refreshFormValidation();
      }, 100);
    }
  }

  getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (field?.invalid && field?.touched) {
      // Get the display name for the field, with fallback to formatted version
      const displayName = this.fieldDisplayNames[fieldName] || this.formatFieldName(fieldName);
      
      if (field.errors?.['required']) {
        return `${displayName} is required`;
      }
      if (field.errors?.['email']) {
        return `${displayName} must be a valid email address`;
      }
      if (field.errors?.['minlength']) {
        return `${displayName} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  // Helper method to format field names that aren't in our mapping
  private formatFieldName(fieldName: string): string {
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
  }

  canInviteUsers(): boolean {
    // Use currentUser if available, otherwise fall back to loggedInUserRole
    const currentRole = this.currentUser?.roleId?.identification || this.loggedInUserRole;
    
    if (!currentRole) return false;
    
    // Define role hierarchy (from highest to lowest level)
    const roleHierarchy = {
      'SUPER_DEV_ADMIN': 6, // Highest level
      'CSM': 5,
      'SM': 4, 
      'SA': 3,
      'A': 2,
      'FSR': 1  // Lowest level
    };
    
    // Only allow users with roles that can invite others (level 2 and above)
    // FSR (level 1) cannot invite users
    return roleHierarchy[currentRole as keyof typeof roleHierarchy] >= 2;
  }

  canEditUsers(): boolean {
    // Same permission as inviting users
    return this.canInviteUsers();
  }

  isEditingOwnAccount(): boolean {
    return this.isEditMode && this.editingUserId === this.loggedInUserID;
  }

  /**
   * Check if the user being edited has SA role (role changes should be disabled for SA users)
   */
  isEditingUserSA(): boolean {
    if (!this.isEditMode || !this.editingUser?.roleId?.identification) {
      return false;
    }
    return this.editingUser.roleId.identification === 'SA';
  }

  goBack() {
    this.router.navigate(['/admin/user-management']);
  }

  isEditingHigherRoleUser(user: User): boolean {
    if (!this.currentUser || !user.roleId) return false;
    
    // Define role hierarchy (from highest to lowest level)
    const roleHierarchy = {
      'SUPER_DEV_ADMIN': 6, // Highest level
      'CSM': 5,
      'SM': 4, 
      'SA': 3,
      'A': 2,
      'FSR': 1  // Lowest level
    };
    
    const currentUserLevel = roleHierarchy[this.currentUser.roleId.identification as keyof typeof roleHierarchy];
    const targetUserLevel = roleHierarchy[user.roleId.identification as keyof typeof roleHierarchy];
    
    return targetUserLevel > currentUserLevel;
  }

  isAssigningHigherRole(): boolean {
    if (!this.currentUser) return false;

    const selectedRoleId = this.userForm.get('roleId')?.value;
    if (!selectedRoleId) return false;

    const selectedRole = this.roles.find(role => role._id === selectedRoleId);
    if (!selectedRole) return false;

    // Define role hierarchy (from highest to lowest level)
    const roleHierarchy = {
      'SUPER_DEV_ADMIN': 6, // Highest level
      'CSM': 5,
      'SM': 4, 
      'SA': 3,
      'A': 2,
      'FSR': 1  // Lowest level
    };

    const currentUserLevel = roleHierarchy[this.currentUser.roleId.identification as keyof typeof roleHierarchy];
    const targetUserLevel = roleHierarchy[selectedRole.identification as keyof typeof roleHierarchy];

    return targetUserLevel > currentUserLevel;
  }

  isSARoleSelected(): boolean {
    const selectedRoleId = this.userForm.get('roleId')?.value;
    if (!selectedRoleId) {
      console.log('No role selected');
      return false;
    }
    
    const selectedRole = this.roles.find(role => role._id === selectedRoleId);
    const isSA = selectedRole?.identification === 'SA';
    
    console.log('Role check:', {
      selectedRoleId,
      selectedRole: selectedRole ? {
        _id: selectedRole._id,
        identification: selectedRole.identification,
        name: selectedRole.name
      } : null,
      isSA
    });
    
    return isSA;
  }

  isFormValidForSubmission(): boolean {
    // Check if basic required fields are valid
    const firstNameValid = this.userForm.get('firstName')?.valid;
    const lastNameValid = this.userForm.get('lastName')?.valid;
    const emailValid = this.userForm.get('email')?.valid;
    const roleIdValid = this.userForm.get('roleId')?.valid;
    
    // For disabled fields (like email in edit mode), consider them valid if they have a value
    const emailField = this.userForm.get('email');
    const emailValidForSubmission = emailField?.disabled ? 
      (emailField?.value && emailField?.value.trim() !== '') : 
      emailValid;
    
    const basicFieldsValid = firstNameValid && lastNameValid && emailValidForSubmission && roleIdValid;
    
    if (!basicFieldsValid) {
      return false;
    }
    
    // If SA role is selected, company name must also be valid
    if (this.isSARoleSelected()) {
      const companyNameControl = this.userForm.get('companyName');
      const companyNameValid = companyNameControl?.valid || false;
      
      if (!companyNameValid) {
        return false;
      }
    }
    
    return true;
  }

  onCompanyNameChange() {
    // Trigger form validation update when company name changes
    if (this.isSARoleSelected()) {
      this.userForm.get('companyName')?.updateValueAndValidity();
      this.userForm.updateValueAndValidity();
    }
  }

  // Force refresh form validation state
  refreshFormValidation() {
    // Mark all fields as touched to trigger validation display
    Object.keys(this.userForm.controls).forEach(key => {
      const control = this.userForm.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });
    
    // Update the entire form
    this.userForm.updateValueAndValidity();
  }

  // Check if subscriber ID is automatically populated
  isSubscriberIdAutoPopulated(): boolean {
    return !this.isEditMode && !!this.currentUser?.subscriberId?._id;
  }

  // Get the auto-populated subscriber company name
  getAutoPopulatedSubscriberName(): string {
    return this.currentUser?.subscriberId?.companyName || '';
  }

  // Load products and territories for the current subscriber
  loadProductsAndTerritories() {
    console.log('🔄 Starting to load products and territories...');
    this.loadProductsForSubscriber();
    this.loadTerritoriesForSubscriber();
  }


  // Load active products for the subscriber
  loadProductsForSubscriber() {
    console.log('🔄 Loading products - setting loadingProducts to true');
    this.loadingProducts = true;
          this.productService.getPagedProducts({
        pageIndex: 1,
        pageSize: 1000, // Get all products
        filters: {
          isActive: true,
          isArchived: false
        }
      })
      .subscribe({
        next: (response) => {
          console.log('✅ Products loaded successfully');
          this.products = response.data.map(p => ({
            _id: p._id,
            name: p.name,
            code: p.code,
            isActive: p.isActive
          }));
          
          // In add mode, select all products by default
          if (!this.isEditMode) {
            this.selectedProductIds = this.products.map(p => p._id);
            this.userForm.patchValue({ products: this.selectedProductIds });
          }
          
          this.loadingProducts = false;
        },
        error: (error) => {
          console.error('❌ Error loading products:', error);
          
          // Extract the actual error message from the API response
          let errorMessage = 'Failed to load products';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
          this.loadingProducts = false;
        }
      });
  }

  // Load active territories for the subscriber
  loadTerritoriesForSubscriber() {
    console.log('🔄 Loading territories - setting loadingTerritories to true');
    this.loadingTerritories = true;
    this.territoryService.getTerritoriesGroupedByRegion()
      .subscribe({
        next: (groupedTerritories) => {
          console.log('✅ Territories loaded successfully');
          this.territoriesGrouped = groupedTerritories;
          
          // In add mode, select all territories by default
          if (!this.isEditMode) {
            this.selectedTerritoryIds = this.getAllTerritoryIds();
            this.userForm.patchValue({ territories: this.selectedTerritoryIds });
          }
          
          this.loadingTerritories = false;
        },
        error: (error) => {
          console.error('❌ Error loading territories:', error);
          
          // Extract the actual error message from the API response
          let errorMessage = 'Failed to load territories';
          if (error.error?.message) {
            errorMessage = error.error.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage
          });
          this.loadingTerritories = false;
        }
      });
  }

  // Get all territory IDs from grouped territories
  getAllTerritoryIds(): string[] {
    const ids: string[] = [];
    Object.values(this.territoriesGrouped).forEach(territories => {
      territories.forEach(territory => {
        if (territory.status === 'Active') {
          ids.push(territory._id);
        }
      });
    });
    return ids;
  }

  // Get regions for accordion
  getRegions(): string[] {
    return Object.keys(this.territoriesGrouped);
  }

  // Get territories for a specific region
  getTerritoriesForRegion(region: string): TerritoryItem[] {
    return this.territoriesGrouped[region]?.filter(t => t.status === 'Active') || [];
  }

  // Check if product is selected
  isProductSelected(productId: string): boolean {
    return this.selectedProductIds.includes(productId);
  }

  // Check if territory is selected
  isTerritorySelected(territoryId: string): boolean {
    return this.selectedTerritoryIds.includes(territoryId);
  }

  // Toggle product selection
  onProductToggle(productId: string, checked: boolean) {
    if (checked) {
      if (!this.selectedProductIds.includes(productId)) {
        this.selectedProductIds.push(productId);
      }
    } else {
      const index = this.selectedProductIds.indexOf(productId);
      if (index > -1) {
        this.selectedProductIds.splice(index, 1);
      }
    }
    this.userForm.patchValue({ products: this.selectedProductIds });
  }

  // Toggle territory selection
  onTerritoryToggle(territoryId: string, checked: boolean) {
    if (checked) {
      if (!this.selectedTerritoryIds.includes(territoryId)) {
        this.selectedTerritoryIds.push(territoryId);
      }
    } else {
      const index = this.selectedTerritoryIds.indexOf(territoryId);
      if (index > -1) {
        this.selectedTerritoryIds.splice(index, 1);
      }
    }
    this.userForm.patchValue({ territories: this.selectedTerritoryIds });
  }

  // Check if all products in region are selected
  areAllProductsSelected(): boolean {
    return this.products.length > 0 && this.products.every(p => this.isProductSelected(p._id));
  }

  // Check if all territories in region are selected
  areAllTerritoriesInRegionSelected(region: string): boolean {
    const territories = this.getTerritoriesForRegion(region);
    return territories.length > 0 && territories.every(t => this.isTerritorySelected(t._id));
  }

  // Toggle all products
  onToggleAllProducts(checked: boolean) {
    if (checked) {
      this.selectedProductIds = this.products.map(p => p._id);
    } else {
      this.selectedProductIds = [];
    }
    this.userForm.patchValue({ products: this.selectedProductIds });
  }

  // Toggle all territories in region
  onToggleAllTerritoriesInRegion(region: string, checked: boolean) {
    const territories = this.getTerritoriesForRegion(region);
    
    if (checked) {
      territories.forEach(territory => {
        if (!this.selectedTerritoryIds.includes(territory._id)) {
          this.selectedTerritoryIds.push(territory._id);
        }
      });
    } else {
      territories.forEach(territory => {
        const index = this.selectedTerritoryIds.indexOf(territory._id);
        if (index > -1) {
          this.selectedTerritoryIds.splice(index, 1);
        }
      });
    }
    this.userForm.patchValue({ territories: this.selectedTerritoryIds });
  }

  // File upload methods for ID Card (only in edit mode)
  private uploadImage(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isFileUploading = true;
      
      // Store the file object for preview purposes
      this.uploadedFile = file;
      
      this.fileService.postFile(file, "USER_IMAGE_UPLOAD").subscribe({
        next: (res) => {
          console.log('AddEditUser: Upload successful', res);
          this.isFileUploading = false;
          const data = res as any;
          
          // Store the uploaded file URL
          this.uploadedFileUrl = data.url;
          
          // Update the form with the uploaded file URL
          this.userForm.patchValue({
            idCard: data.url
          });
          
          this.fileService.fileUploadingSubject.next({
            type: "USER_IMAGE_UPLOAD",
            loading: false,
          });

          this.messageService.add({
            severity: 'success',
            summary: 'File uploaded successfully',
            detail: `${file.name} has been uploaded`
          });
          
          resolve();
        },
        error: (error) => {
          console.error('AddEditUser: Upload error', error);
          this.isFileUploading = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Upload failed',
            detail: error.error?.message || `Failed to upload ${file.name}`
          });
          reject(error);
        }
      });
    });
  }

  removeUploadedFile(): void {
    this.uploadedFileUrl = null;
    this.uploadedFile = null;
    this.userForm.patchValue({
      idCard: ''
    });
    
    // Clear the file input
    this.resetFileInput();
    
    this.messageService.add({
      severity: 'info',
      summary: 'File removed',
      detail: 'File has been removed'
    });
  }

  // Reset file input element
  private resetFileInput(): void {
    const fileInput = document.getElementById('file-input-idCard-add-edit') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // Method to check if a file is an image
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Drag and drop methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    
    // Use centralized validation
    const validation = this.fileService.validateFile(file, 'USER_IMAGE_UPLOAD');
    if (!validation.isValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'File validation failed',
        detail: validation.errors.join(', ')
      });
      return;
    }

    // Upload the file
    this.uploadImage(file).then(() => {
      // Reset file input after upload completes
      this.resetFileInput();
    }).catch(() => {
      // Reset even on error
      this.resetFileInput();
    });
  }

  onFileUpload(event: Event | any): void {
    // Handle both native input change event and other event types
    let files: FileList | null = null;
    
    if (event.target) {
      // Native input change event
      const target = event.target as HTMLInputElement;
      files = target.files;
    } else if (event.currentTarget) {
      // Fallback for currentTarget
      const target = event.currentTarget as HTMLInputElement;
      files = target.files;
    } else if (event.files) {
      // PrimeNG FileUpload event (if used)
      files = event.files;
    }
    
    if (files && files.length > 0) {
      const file = files[0];
      
      // Use centralized validation
      const validation = this.fileService.validateFile(file, 'USER_IMAGE_UPLOAD');
      if (!validation.isValid) {
        this.messageService.add({
          severity: 'error',
          summary: 'File validation failed',
          detail: validation.errors.join(', ')
        });
        // Reset file input on validation error
        this.resetFileInput();
        return;
      }
      
      this.uploadImage(file).then(() => {
        // Reset file input after upload completes to allow re-uploading the same file
        this.resetFileInput();
      }).catch(() => {
        // Reset even on error
        this.resetFileInput();
      });
    }
  }

}
